import { watch } from 'vue';
import type { UseQueryReturnType } from '@tanstack/vue-query';

type OnSuccessParams<TData, TError> = {
  query: UseQueryReturnType<TData, TError>;
  /**
   * @description Query can be cached before the handler is added.
   * In that case it will not be called since watch does not spot changes
   *
   * If we need to call the handler after data is cached
   * then we should use set shouldCallOnCache to true
   */
  shouldCallOnCache?: boolean;
  onSuccess: (data: TData) => void;
};

const withOnSuccess = <TData, TError>({
  query,
  onSuccess,
  shouldCallOnCache,
}: OnSuccessParams<TData, TError>) => {
  /**
   * Query can be cached, the watch block won't be called
   * We need to check if the query already has resolved with success
   */
  if (shouldCallOnCache && query.isSuccess.value) {
    onSuccess(query.data.value);
  }

  watch(query.isSuccess, isSuccess => {
    if (isSuccess) {
      onSuccess(query.data.value);
    }
  });
};

type OnErrorParams<TData, TError> = {
  onError: (error: TError) => void;
  query: UseQueryReturnType<TData, TError>;
};

const withOnError = <TData, TError>({
  query,
  onError,
}: OnErrorParams<TData, TError>) => {
  watch(query.isError, isError => {
    if (isError) {
      onError(query.error.value);
    }
  });
};

export const vueQueryLib = { withOnSuccess, withOnError };
